import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import * as Flags from "country-flag-icons/react/3x2";

const FLAG_ORDER = ["BR", "US", "PT", "ES", "FR", "IT"];
const LBL = {
  pt: { select: "Selecionar" }, "pt-PT": { select: "Selecionar" }, en: { select: "Select" }, es: { select: "Seleccionar" },
  fr: { select: "Sélectionner" }, it: { select: "Seleziona" },
};

// Country/Region selector — a compact row of premium flag buttons.
// A flag = MARKET (currency + price + default language). It NEVER shows price/currency/code.
// Clicking a flag switches market AND the interface to that market's default language
// (handled by the parent's onChange). Changing language never changes the country.
export default function MarketSelector({ markets = [], value, onChange, lang = "pt" }) {
  const L = LBL[lang] || LBL.pt;
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, []);
  const byCode = Object.fromEntries(markets.map((m) => [m.code, m]));
  const ordered = FLAG_ORDER.filter((c) => byCode[c]);
  if (!ordered.length) return null;
  const ActiveFlag = Flags[value];
  const groupLabel = lang.startsWith("pt") ? "Selecionar país ou região" : "Select country or region";

  return (
    <>
    {/* Mobile: active flag + dropdown with all markets (fits any phone width) */}
    <div className="relative sm:hidden" ref={ref} data-testid="market-selector-mobile">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={open} aria-label={groupLabel} data-testid="market-selector-mobile-button"
        className="flex items-center gap-1.5 rounded-full border border-xmas-gold/25 bg-xmas-navycard/70 px-2 py-1.5">
        <span className="flex h-5 w-7 items-center justify-center overflow-hidden rounded-[4px] ring-1 ring-xmas-gold/60">
          {ActiveFlag ? <ActiveFlag className="h-full w-full object-cover" /> : <span className="text-[10px] text-xmas-snow">{value}</span>}
        </span>
        <ChevronDown className={`h-3.5 w-3.5 text-xmas-slate transition ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul role="listbox" data-testid="market-menu-mobile" className="absolute right-0 z-50 mt-2 w-56 overflow-hidden rounded-xl border border-xmas-gold/25 bg-xmas-navycard shadow-2xl">
          {ordered.map((code) => {
            const Flag = Flags[code]; const active = code === value; const name = byCode[code].name?.[lang] || code;
            return (
              <li key={code} role="option" aria-selected={active}>
                <button type="button" data-testid={`market-flag-mobile-${code}`} onClick={() => { onChange?.(code); setOpen(false); }}
                  className={`flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm transition ${active ? "bg-xmas-gold/10 text-xmas-gold" : "text-xmas-snow hover:bg-white/5"}`}>
                  <span className="flex h-5 w-7 shrink-0 items-center justify-center overflow-hidden rounded-[4px]">{Flag && <Flag className="h-full w-full object-cover" />}</span>
                  <span className="flex-1">{name}</span>
                  {active && <Check className="h-4 w-4" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
    {/* Desktop/tablet: full row of flags */}
    <div role="group" aria-label={groupLabel}
      className="hidden items-center gap-2.5 rounded-full border border-xmas-gold/20 bg-xmas-navycard/60 px-2.5 py-1 sm:flex"
      data-testid="market-selector">
      {ordered.map((code) => {
        const m = byCode[code];
        const Flag = Flags[code];
        const active = code === value;
        const name = m.name?.[lang] || code;
        return (
          <div key={code} className="group relative">
            <button
              type="button"
              data-testid={`market-flag-${code}`}
              aria-label={`${L.select} ${name}`}
              aria-pressed={active}
              onClick={() => onChange?.(code)}
              className={`relative flex h-7 w-9 items-center justify-center overflow-hidden rounded-md ring-offset-1 ring-offset-xmas-navycard transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-xmas-gold ${active ? "scale-110 ring-2 ring-xmas-gold" : "opacity-70 hover:opacity-100 hover:scale-105"}`}
            >
              {Flag ? <Flag className="h-full w-full object-cover" title={name} /> : <span className="text-xs text-xmas-snow">{code}</span>}
              {active && (
                <span className="absolute -right-0.5 -top-0.5 flex h-3 w-3 items-center justify-center rounded-full bg-xmas-gold" data-testid={`market-flag-active-${code}`}>
                  <Check className="h-2 w-2 text-xmas-navy" strokeWidth={4} />
                </span>
              )}
            </button>
            {/* Tooltip (desktop hover) */}
            <div className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 hidden -translate-x-1/2 whitespace-nowrap rounded-lg border border-xmas-gold/25 bg-xmas-navy px-2.5 py-1 text-xs text-xmas-snow opacity-0 shadow-xl transition-opacity duration-200 group-hover:opacity-100 sm:block">
              {name}
            </div>
          </div>
        );
      })}
    </div>
    </>
  );
}
